/**
 * VAMP-proxy dispute-ratio math (trailing-30d chargebacks ÷ orders; Visa VAMP
 * "excessive" = 1.5%). Shared by admin deal-sizing and merchant dashboards.
 */

export const VAMP_PROXY_EXCESSIVE_THRESHOLD_RATE = 0.015;
export const VAMP_PROXY_MIN_MONTHLY_DISPUTE_COUNT = 1500;

export const vampProxyRiskLevels = [
  "no_data",
  "low",
  "watch",
  "near",
  "over_ratio",
  "exceeds_program",
] as const;

export type VampProxyRiskLevel = (typeof vampProxyRiskLevels)[number];

export interface VampProxy {
  chargebackCountTrailing30d: number;
  orderCountTrailing30d: number;
  ratioTrailing30d: number | null;
  thresholdRatio: number;
  minimumMonthlyDisputeCount: number;
  riskLevel: VampProxyRiskLevel;
}

export function classifyVampProxyRisk({
  chargebackCountTrailing30d,
  orderCountTrailing30d,
  ratioTrailing30d,
}: {
  chargebackCountTrailing30d: number;
  orderCountTrailing30d: number;
  ratioTrailing30d: number | null;
}): VampProxyRiskLevel {
  if (chargebackCountTrailing30d <= 0) {
    return "low";
  }
  if (orderCountTrailing30d <= 0 || ratioTrailing30d == null) {
    return "no_data";
  }
  if (
    ratioTrailing30d >= VAMP_PROXY_EXCESSIVE_THRESHOLD_RATE &&
    chargebackCountTrailing30d >= VAMP_PROXY_MIN_MONTHLY_DISPUTE_COUNT
  ) {
    return "exceeds_program";
  }
  if (ratioTrailing30d >= VAMP_PROXY_EXCESSIVE_THRESHOLD_RATE) {
    return "over_ratio";
  }
  if (ratioTrailing30d >= VAMP_PROXY_EXCESSIVE_THRESHOLD_RATE * 0.8) {
    return "near";
  }
  if (ratioTrailing30d >= VAMP_PROXY_EXCESSIVE_THRESHOLD_RATE * 0.5) {
    return "watch";
  }
  return "low";
}

export function computeVampProxy({
  chargebackCountTrailing30d,
  orderCountTrailing30d,
}: {
  chargebackCountTrailing30d: number;
  orderCountTrailing30d: number;
}): VampProxy {
  let ratioTrailing30d: number | null;
  if (chargebackCountTrailing30d <= 0) {
    ratioTrailing30d = 0;
  } else if (orderCountTrailing30d > 0) {
    ratioTrailing30d = chargebackCountTrailing30d / orderCountTrailing30d;
  } else {
    ratioTrailing30d = null;
  }

  return {
    chargebackCountTrailing30d,
    orderCountTrailing30d,
    ratioTrailing30d,
    thresholdRatio: VAMP_PROXY_EXCESSIVE_THRESHOLD_RATE,
    minimumMonthlyDisputeCount: VAMP_PROXY_MIN_MONTHLY_DISPUTE_COUNT,
    riskLevel: classifyVampProxyRisk({
      chargebackCountTrailing30d,
      orderCountTrailing30d,
      ratioTrailing30d,
    }),
  };
}
